/**
 * FeeGateScreen — full-screen block shown to a student/parent when their
 * account is fee-locked with "all" features. Replaces every portal page so
 * the user sees only a message + their balance.
 */
import React from 'react';
import { Lock, AlertTriangle, LogOut, Mail, Phone } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { formatCurrency as fmtCurrency } from '@/lib/currency';

const fmtMoney = (amount, currency) => fmtCurrency(amount, currency || 'USD');

export default function FeeGateScreen({ status, onLogout }) {
  const { balance = 0, currency = 'USD', message = '', students = [] } = status || {};
  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-rose-50 via-amber-50 to-rose-100 p-4" data-testid="fee-gate-screen">
      <Card className="w-full max-w-xl border-rose-200 border-2 shadow-xl">
        <CardContent className="p-8 space-y-5">
          <div className="flex items-center gap-3">
            <div className="h-12 w-12 rounded-full bg-rose-100 flex items-center justify-center">
              <Lock className="h-6 w-6 text-rose-600" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-slate-900">Access Restricted</h1>
              <p className="text-sm text-slate-600">Your portal access is paused</p>
            </div>
          </div>

          <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900 flex items-start gap-2">
            <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
            <span data-testid="fee-gate-message">
              {message || 'Your account has restricted access due to outstanding fees. Please contact the school administration to clear your balance.'}
            </span>
          </div>

          {balance > 0 && (
            <div className="rounded-lg bg-slate-900 text-white p-5" data-testid="fee-gate-balance">
              <p className="text-xs uppercase tracking-wider text-slate-400">Outstanding balance</p>
              <p className="text-3xl font-bold mt-1">{fmtMoney(balance, currency)}</p>
            </div>
          )}

          {students.length > 1 && (
            <div className="space-y-2">
              <p className="text-xs font-medium text-slate-600 uppercase">Per-student breakdown</p>
              {students.map((s) => (
                <div key={s.id} className="flex items-center justify-between text-sm border rounded-md px-3 py-2" data-testid={`fee-gate-student-${s.id}`}>
                  <div>
                    <span className="font-medium">{s.name}</span>
                    <span className="text-xs text-slate-500 ml-2">{s.admission_number}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    {s.fee_locked && <Badge className="bg-rose-100 text-rose-700">Locked</Badge>}
                    <span className="font-semibold">{fmtMoney(s.balance, currency)}</span>
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="rounded-lg bg-slate-50 border p-4 text-sm text-slate-700 space-y-1">
            <p className="font-medium text-slate-900 mb-2">Next steps</p>
            <p className="flex items-start gap-2">
              <Phone className="h-4 w-4 mt-0.5 shrink-0 text-slate-400" />
              <span>Contact the school accounts office to settle your balance.</span>
            </p>
            <p className="flex items-start gap-2">
              <Mail className="h-4 w-4 mt-0.5 shrink-0 text-slate-400" />
              <span>You will regain full access automatically once your account is cleared.</span>
            </p>
          </div>

          <Button
            variant="outline"
            className="w-full"
            onClick={onLogout}
            data-testid="fee-gate-logout"
          >
            <LogOut className="h-4 w-4 mr-2" /> Log out
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
